import Cropper from 'react-easy-crop';
import { StanModal } from '@/components';
import type { StanCropProps } from './StanCrop.types';
import { useCropImage } from './StanCrop.logic';
import styles from './StanCrop.module.css';

export function StanCrop({
    isOpen,
    imageFile,
    aspectRatio,
    title = 'Crop, Zoom, or Move',
    primaryActionLabel = 'Use this photo',
    isLoading = false,
    width = 400,
    height = 400,
    borderRadius = 0,
    onClose,
    onConfirm,
}: StanCropProps) {
    // Calculate aspect ratio from width/height if not explicitly provided
    const calculatedAspectRatio = aspectRatio ?? width / height;

    const {
        crop,
        zoom,
        croppedAreaPixels,
        isProcessing,
        imageUrl,
        showCropper,
        onCropComplete,
        onCropChange,
        onZoomChange,
        handleConfirm,
    } = useCropImage(imageFile, isOpen, calculatedAspectRatio, borderRadius, onConfirm);

    if (!isOpen || !imageFile || !imageUrl) {
        return null;
    }

    return (
        <StanModal
            key={imageUrl} // Force remount when image changes
            id='stan-crop-component'
            isOpen={isOpen}
            title={title}
            onClose={onClose}
            primaryActionLabel={primaryActionLabel}
            primaryActionLoading={isProcessing || isLoading}
            primaryActionDisabled={!croppedAreaPixels || isLoading}
            onPrimaryAction={handleConfirm}
            secondaryActionLabel='Cancel'
            onSecondaryAction={onClose}
            className={styles.cropModal}
            nonDismissible
            hideCloseButton
        >
            <div className={styles.cropContainer}>
                <div
                    className={styles.cropArea}
                    style={
                        {
                            '--crop-height': `${height}px`,
                        } as React.CSSProperties
                    }
                >
                    {showCropper && imageUrl && (
                        <Cropper
                            image={imageUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={calculatedAspectRatio}
                            onCropChange={onCropChange}
                            onZoomChange={onZoomChange}
                            onCropComplete={onCropComplete}
                            cropShape={borderRadius === 'circle' ? 'round' : 'rect'}
                            showGrid
                            restrictPosition
                            zoomSpeed={1}
                            minZoom={1}
                            maxZoom={3}
                        />
                    )}
                </div>

                <div className={styles.zoomControls}>
                    <div className={styles.sliderContainer}>
                        <input
                            id='zoom-slider'
                            type='range'
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={e => onZoomChange(Number(e.target.value))}
                            className={styles.zoomSlider}
                            style={{ '--zoom-percentage': `${((zoom - 1) / (3 - 1)) * 100}%` } as React.CSSProperties}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            </div>
        </StanModal>
    );
}
